/**
 * The mark, drawn inline so it follows the skin.
 *
 * ## Why this is a component and not an `<img>`
 *
 * It was `<img src="/logo.svg">`, and an image cannot inherit anything. So when the development
 * module turned the application blue, the wordmark beside it went blue — it is text, and text reads
 * `--color-brand-900` — while the mark stayed green. A green building next to a blue word, which is
 * the one thing a logo may never be.
 *
 * The first fix was a second file, `logo-mono.svg`, for the rail's dark ground. That works for
 * white-on-dark and does nothing for the header, which is a **light** ground and needs the mark in
 * the *skin's* colour rather than in white. A third file would have meant three drawings to keep
 * identical, and the second one already carried a warning about exactly that.
 *
 * Drawn inline, the fills are `fill-brand-400` and `fill-brand-300` — the same tokens every other
 * accent in the product reads. **The mark then follows any skin for free**, including one added
 * later, and there is one drawing again.
 *
 * ## The geometry is `public/logo.svg`, verbatim
 *
 * Every path here is copied from it and must stay copied: that file is still the favicon, the
 * manifest icon, the share card and the listing site's mark, none of which can be a React
 * component. `tools/verify-brand-mark.mjs` fails if the two drift apart.
 *
 * The two tones are not two colours but two *jobs*: `brand` for a light ground, where the mark is
 * the skin's own colour; `light` for the rail, where it is white and the neighbours are held back
 * so the house stays the subject. Flat white throughout would collapse three shapes into one
 * silhouette — the original is deliberately two values, the house being the lighter.
 */

export function BrandMark({
  size = 30,
  tone = "brand",
  className = "",
}: {
  size?: number;
  /** `brand` on a light ground — it takes the skin's colour. `light` on the rail's dark one. */
  tone?: "brand" | "light";
  className?: string;
}) {
  const near = tone === "light" ? "fill-white/60" : "fill-brand-400";
  const house = tone === "light" ? "fill-white" : "fill-brand-300";
  const edge = tone === "light" ? "stroke-white/60" : "stroke-brand-400";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      role="img"
      aria-label="RentorNG"
      className={className}
    >
      <title>RentorNG</title>
      <path fillRule="evenodd" className={near} d="M19 296 135 232v221H19zM45 300h64v58H45zM45 372h64v58H45z" />
      <path
        fillRule="evenodd"
        className={`${house} ${edge}`}
        strokeWidth={10}
        strokeLinejoin="round"
        d="M148 96 364 26v422H148zM256 196a54 54 0 0 0-22 103l-15 77h74l-15-77a54 54 0 0 0-22-103z"
      />
      <path fillRule="evenodd" className={near} d="M377 148 493 212v241H377zM403 232h64v72h-64zM403 344h64v72h-64z" />
    </svg>
  );
}
